// @ts-check

/**
 * Compact (tablet / phone) navigation for the admin dashboard.
 *
 * Desktop keeps the inline tab strip. At `NAV_BREAKPOINT_PX` and below the strip
 * becomes a left-hand drawer with 48px tap targets, opened from a hamburger
 * control so the nine sections no longer overflow the sticky header.
 */

/** Viewport width at which the header swaps the tab strip for the drawer. */
export const NAV_BREAKPOINT_PX = 1100;

/**
 * A document-like root the nav helpers can bind to. Tests pass a stub;
 * the dashboard passes `document`.
 *
 * @typedef {Object} NavRoot
 * @property {HTMLElement} body
 * @property {(id: string) => (HTMLElement|null)} getElementById
 * @property {(selector: string) => (HTMLElement|null)} [querySelector]
 * @property {(selector: string) => (NodeListOf<HTMLElement>|HTMLElement[])} [querySelectorAll]
 * @property {(type: string, listener: (event: KeyboardEvent) => void) => void} [addEventListener]
 * @property {{ innerWidth: number, addEventListener?: Function }} [defaultView]
 */

/**
 * Visible label for a tab button.
 *
 * Compact header uses the short operator name (`data-nav-label`, or the
 * button text without its leading emoji). `title` is the longer tooltip
 * and is too wide for a phone header.
 *
 * @param {{ getAttribute?: Function, textContent?: string|null }|null|undefined} btn
 * @returns {string}
 */
export function tabLabelFromButton(btn) {
  if (!btn) return '';
  const named = typeof btn.getAttribute === 'function' ? (btn.getAttribute('data-nav-label') || '') : '';
  if (named) return named;
  const text = String(btn.textContent || '').replace(/\s+/g, ' ').trim();
  const withoutMark = text.replace(/^[^\p{L}\p{N}]+/u, '').trim();
  if (withoutMark) return withoutMark;
  const titled = typeof btn.getAttribute === 'function' ? (btn.getAttribute('title') || '') : '';
  return titled || text;
}

/**
 * Whether the current viewport uses the drawer instead of the tab strip.
 *
 * Unknown widths (Node tests with no window) default to desktop so the
 * strip is not accidentally `inert`.
 *
 * @param {number} [width]
 * @returns {boolean}
 */
export function isCompactNav(width) {
  const resolved = typeof width === 'number' ? width : Number(globalThis.innerWidth);
  if (!Number.isFinite(resolved)) return false;
  return resolved <= NAV_BREAKPOINT_PX;
}

/**
 * Settings that default on. Only an explicit off value hides the matching admin tab.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isDefaultOnNavVisible(value) {
  if (value === undefined || value === null) return true;
  const v = String(value).trim().toLowerCase();
  if (!v) return true;
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

/**
 * Player messages default on. Only an explicit off value hides the admin Messages tab.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isSupportMessagesNavVisible(value) {
  return isDefaultOnNavVisible(value);
}

/**
 * Shows or hides one nav button from a default-on setting.
 *
 * @param {{ getElementById?: (id: string) => any }|null|undefined} root
 * @param {string} id
 * @param {unknown} value
 */
export function applyNavButton(root, id, value) {
  const btn = root && typeof root.getElementById === 'function'
    ? root.getElementById(id)
    : null;
  if (!btn) return;
  btn.hidden = !isDefaultOnNavVisible(value);
}

/**
 * Shows or hides the Messages nav button.
 *
 * @param {{ getElementById?: (id: string) => any }|null|undefined} root
 * @param {unknown} value
 */
export function applySupportMessagesNav(root, value) {
  applyNavButton(root, 'tab-messages-btn', value);
}

/**
 * Shows or hides the Errors nav button.
 *
 * @param {{ getElementById?: (id: string) => any }|null|undefined} root
 * @param {unknown} value
 */
export function applyErrorCollectionNav(root, value) {
  applyNavButton(root, 'tab-errors-btn', value);
}

/**
 * Settings that default off. Only an explicit on value shows the matching admin tab.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isDefaultOffNavVisible(value) {
  if (value === undefined || value === null) return false;
  const v = String(value).trim().toLowerCase();
  if (!v) return false;
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

/**
 * Shows or hides the Bot nav button. Hosting defaults off, so a missing
 * value keeps the tab hidden.
 *
 * @param {{ getElementById?: (id: string) => any }|null|undefined} root
 * @param {unknown} value
 */
export function applyBotHostingNav(root, value) {
  const btn = root && typeof root.getElementById === 'function'
    ? root.getElementById('tab-bot-btn')
    : null;
  if (!btn) return;
  btn.hidden = !isDefaultOffNavVisible(value);
}

/**
 * @param {NavRoot|Document} [root]
 * @returns {boolean}
 */
export function isNavOpen(root) {
  const body = (root && /** @type {NavRoot} */ (root).body) || (typeof document !== 'undefined' ? document.body : null);
  return !!body && body.classList.contains('nav-open');
}

/**
 * @param {NavRoot|Document} root
 * @returns {{ innerWidth: number, addEventListener?: Function }}
 */
function viewOf(root) {
  const candidate = /** @type {NavRoot} */ (root).defaultView;
  if (candidate && typeof candidate.innerWidth === 'number') return candidate;
  return { innerWidth: Number(globalThis.innerWidth) || NAV_BREAKPOINT_PX + 1 };
}

/**
 * Marks the drawer inert while it is off-canvas, and restores it on desktop
 * or while it is open. Desktop never hides the strip from assistive tech.
 *
 * @param {NavRoot|Document} root
 * @param {boolean} compact
 * @param {boolean} open
 */
export function applyDrawerAvailability(root, compact, open) {
  const drawer = root.getElementById('admin-nav');
  if (!drawer) return;
  const hide = compact && !open;
  if (hide) {
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
  } else {
    drawer.removeAttribute('inert');
    drawer.removeAttribute('aria-hidden');
  }
}

/**
 * Opens or closes the compact navigation drawer.
 *
 * @param {boolean} open
 * @param {{ root?: NavRoot|Document, restoreFocus?: boolean }} [options]
 */
export function setNavOpen(open, options = {}) {
  const root = options.root || document;
  const body = /** @type {NavRoot} */ (root).body;
  const toggle = root.getElementById('nav-menu-toggle');
  const backdrop = root.getElementById('nav-drawer-backdrop');
  const compact = isCompactNav(viewOf(root).innerWidth);

  if (body) body.classList.toggle('nav-open', open);

  if (toggle) {
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  }
  if (backdrop) {
    if (open && compact) backdrop.removeAttribute('hidden');
    else backdrop.setAttribute('hidden', '');
  }

  applyDrawerAvailability(root, compact, open);

  if (options.restoreFocus === false) return;
  if (open) {
    const closeBtn = root.getElementById('nav-drawer-close');
    (closeBtn || toggle)?.focus?.();
  } else {
    toggle?.focus?.();
  }
}

/**
 * Copies the active tab's title into the compact header label.
 *
 * @param {NavRoot|Document} [root]
 */
export function syncNavCurrentLabel(root) {
  const doc = root || document;
  const label = doc.getElementById('nav-current-label');
  if (!label) return;
  const active = typeof doc.querySelector === 'function'
    ? doc.querySelector('.nav-tab-btn.active')
    : null;
  label.textContent = tabLabelFromButton(active) || 'Menu';
}

/**
 * Closes an open drawer when the viewport grows past the compact breakpoint,
 * and keeps `inert` in sync when the operator rotates a tablet.
 *
 * @param {NavRoot|Document} [root]
 */
export function syncNavLayout(root) {
  const doc = root || document;
  const compact = isCompactNav(viewOf(doc).innerWidth);
  const open = isNavOpen(doc);
  if (!compact && open) {
    setNavOpen(false, { root: doc, restoreFocus: false });
    return;
  }
  applyDrawerAvailability(doc, compact, open);
}

/**
 * Wires hamburger, backdrop, Escape, tab-select-closes, and resize.
 *
 * @param {NavRoot|Document} [root]
 */
export function initAdminNav(root) {
  const doc = root || document;
  const toggle = doc.getElementById('nav-menu-toggle');
  const closeBtn = doc.getElementById('nav-drawer-close');
  const backdrop = doc.getElementById('nav-drawer-backdrop');
  const tabs = typeof doc.querySelectorAll === 'function'
    ? doc.querySelectorAll('.nav-tab-btn')
    : [];

  syncNavCurrentLabel(doc);
  setNavOpen(false, { root: doc, restoreFocus: false });

  toggle?.addEventListener('click', () => {
    setNavOpen(!isNavOpen(doc), { root: doc });
  });
  closeBtn?.addEventListener('click', () => {
    setNavOpen(false, { root: doc });
  });
  backdrop?.addEventListener('click', () => {
    setNavOpen(false, { root: doc });
  });

  if (typeof doc.addEventListener === 'function') {
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isNavOpen(doc)) {
        setNavOpen(false, { root: doc });
      }
    });
  }

  Array.from(tabs).forEach((tab) => {
    tab.addEventListener('click', () => {
      syncNavCurrentLabel(doc);
      setNavOpen(false, { root: doc, restoreFocus: false });
    });
  });

  const win = viewOf(doc);
  if (typeof win.addEventListener === 'function') {
    win.addEventListener('resize', () => {
      syncNavLayout(doc);
    });
  }
}
